import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { fr } from "yup-locales";
import { setLocale } from "yup";
import { useAuthByCodeMutation } from "@/redux/services/auth/auth.api";
import { QueryError } from "@/lib/type";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { setIsAuthenticated } from "@/redux/features/auth/auth.slice";
import { useAppDispatch } from "@/redux/features/hooks";
import { useFetchCurrentUserAndRestaurant } from "./useFetchCurrentUserAndRestaurant";
import { logger } from "@/services/Logger";
import { getSecurityErrorMessage } from "@/lib/securityErrors";
import { getApiErrorMessage } from "@/lib/apiErrors";

setLocale(fr);

type AuthCodeFormValues = {
  authCode: string;
};

const loginFormSchema = yup.object().shape({
  authCode: yup
    .string()
    .required("Veuillez entrer le code d'authentification"),
});

// Window of the API's per-account login limiter (SFAPI-168).
const LOGIN_LOCKOUT_MINUTES = 15;

/**
 * Message for a 429 `security.error.too_many_attempts` from POST /api/auth_code.
 * The API clears a pending SMS code at lockout, so SMS users need a new one.
 */
export const tooManyAttemptsMessage = (
  requiresPin: boolean,
  retryAfterMinutes?: number | null,
): string => {
  const minutes = retryAfterMinutes ?? LOGIN_LOCKOUT_MINUTES;

  return requiresPin
    ? `Trop de tentatives. Réessayez dans ${minutes} minutes.`
    : `Trop de tentatives. Réessayez dans ${minutes} minutes avec un nouveau code.`;
};

export const useAuthByCodeForm = () => {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
    reset,
  } = useForm<AuthCodeFormValues>({
    resolver: yupResolver(loginFormSchema),
  });

  const { state } = useLocation();
  const navigate = useNavigate();

  const [sendCode, { isLoading }] = useAuthByCodeMutation();
  const dispatch = useAppDispatch();
  const { fetchUserAndRestaurant } = useFetchCurrentUserAndRestaurant();

  const phone: string | undefined = state?.phone;
  const requiresPin: boolean = state?.requiresPin === true;

  const onSubmit = async (data: AuthCodeFormValues) => {
    if (!phone) {
      navigate("/auth/login", { replace: true });
      return;
    }

    const res = await sendCode({
      emailOrPhone: phone,
      authCode: data.authCode,
    });

    if ("data" in res) {
      toast.success("Connexion réussie !");
      reset();
      localStorage.removeItem("phone");
      dispatch(setIsAuthenticated(true));

      try {
        await fetchUserAndRestaurant();
        // Let the root route resolve the role-based landing page
        // (manager/owner → dashboard, cashier/waiter → hub, kitchen → kitchen).
        navigate("/");
      } catch (error) {
        logger.error("useAuthByCodeForm", "Error fetching user data:", error);
      }
    } else if ("error" in res) {
      const error = res.error as QueryError;
      const errorMessage = error?.data?.message;
      if (errorMessage === "security.error.auth_code_invalid") {
        setError("authCode", {
          message: requiresPin ? "Code PIN invalide" : "Code invalide",
        });
        return;
      } else if (errorMessage === "security.error.too_many_attempts") {
        const retryAfterMinutes = (
          error?.data as { retryAfterMinutes?: number | null } | undefined
        )?.retryAfterMinutes;
        const message = tooManyAttemptsMessage(requiresPin, retryAfterMinutes);
        if (requiresPin) {
          setError("authCode", { message });
        } else {
          toast.error(message);
          navigate("/auth/login", { replace: true });
        }
        return;
      } else if (errorMessage === "security.error.auth_code_expired") {
        toast.error("Le code a expiré, veuillez en renvoyer un nouveau");
        navigate("/auth/login", {
          replace: true,
        });
      }
      const message =
        getSecurityErrorMessage(errorMessage) ??
        getApiErrorMessage(error, "Une erreur inconnue est survenue");
      setError("authCode", { message: message });
    }
  };

  return {
    register,
    errors,
    onSubmit: handleSubmit(onSubmit),
    isLoading,
    phone,
    requiresPin,
  };
};
